import React from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceArea } from "recharts";
import { useNavigate } from "react-router-dom";

function TextBlock({ text }) {
  return (
    <div className="prose prose-neutral max-w-none">
      {text.split(/\n\n+/).map((p, i) => (
        <p key={i} className="text-neutral-800 leading-relaxed my-2 whitespace-pre-wrap">{p}</p>
      ))}
    </div>
  );
}

function ChartBlock({ block }) {
  const navigate = useNavigate();
  const data = (block.points || []).map((p) => ({
    date: p.date,
    value: p.value,
  }));
  return (
    <button
      onClick={() => navigate(`/observation/${block.canonical_type}`)}
      data-testid={`chart-${block.canonical_type}`}
      className="w-full text-left bg-white rounded-2xl border border-neutral-200 p-4 hover:border-neutral-300 transition-colors"
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-semibold text-neutral-900">{block.title}</div>
        <div className="text-xs text-neutral-500">{block.unit} · {data.length} readings</div>
      </div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="date" hide />
            <YAxis hide domain={["auto", "auto"]} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e5e5" }}
              formatter={(v) => [`${v} ${block.unit || ""}`, block.title]}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={block.color || "#F6CFBE"}
              strokeWidth={2.5}
              dot={{ r: 2, fill: block.color || "#F6CFBE" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="text-xs text-neutral-500 mt-2">Tap to explore →</div>
    </button>
  );
}

function MomentBlock({ block }) {
  return (
    <div className="bg-gradient-to-br from-[#F6CFBE]/40 to-[#B9DCF2]/40 rounded-2xl p-5 border border-neutral-100">
      <div className="text-xs uppercase tracking-wide text-neutral-600 mb-1">Moment</div>
      <div className="font-semibold text-neutral-900">{block.heading}</div>
      <div className="text-sm text-neutral-700 mt-1">{block.body}</div>
    </div>
  );
}

function EvidenceBlock({ block }) {
  const navigate = useNavigate();
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Based on</div>
      <div className="flex flex-wrap gap-2">
        {(block.items || []).map((it, i) => (
          <button
            key={i}
            onClick={() => it.event_id && navigate(`/events/${it.event_id}`)}
            className="text-xs px-3 py-1.5 rounded-full bg-white border border-neutral-200 hover:border-neutral-300"
          >
            {it.title || it.type || "Record"} {it.date ? `· ${it.date}` : ""}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function BlockRenderer({ blocks }) {
  if (!blocks || blocks.length === 0) return null;
  return (
    <div className="space-y-4">
      {blocks.map((b, i) => {
        if (b.kind === "text") return <TextBlock key={i} text={b.text} />;
        if (b.kind === "chart") return <ChartBlock key={i} block={b} />;
        if (b.kind === "moment") return <MomentBlock key={i} block={b} />;
        if (b.kind === "evidence") return <EvidenceBlock key={i} block={b} />;
        return null;
      })}
    </div>
  );
}
